import React, { ChangeEventHandler, Dispatch, SetStateAction, useContext, useEffect, useState } from 'react'
import { CardContainer, ContentContainer, MainContainer } from '../../focus_view_shared.styles'
import { CardHeader, Text } from '@amzn/storm-ui'
import {
  BoldText,
  EmailFieldEntryContainer,
  FormFieldNameContainer,
  FormFieldValueContainer,
  FormRowContainer,
  InviteDataEntryContainer,
  StyledAlert,
  StyledCard,
  TextAreaFormGroupStyled,
  TopAlertContainer,
} from '../bulk_users_invite.styles'
import { UserInviteRoleInfo } from '../bulk_users_invite_shared'
import { useTranslation } from 'react-i18next'
import { pipe } from 'fp-ts/es6/function'
import { parseListOfInvitees } from '../bulk_invite_logic'
import * as i18nKeyList from '../bulk_users_invite_i18n_mapping'
import { getUserManagementURL } from '../../ads_console_utils'
import * as AACChromeAPI from '@amzn/aac-chrome-api'
import { useLoginContext } from 'stagedive/LoginContext'
import { toNullable } from 'fp-ts/es6/Option'
import { StageDiveConfig, StageDiveConfigContext } from '../../../../../../util/app_initialization/appconfig'

export type UsersInviteDataEntryError = 'TooManyEntries' | 'EmptyContent'
interface UsersInviteDataEntryProps {
  listOfInvitees: string[]
  setListOfInvitees: (val: string[]) => void
  dataEntryError?: UsersInviteDataEntryError
  nextAction: () => void
  successfulInviteCount: number
  setSuccessfulInviteCount: Dispatch<SetStateAction<number>>
}

export const UsersInviteDataEntry: React.FunctionComponent<UsersInviteDataEntryProps> = (props: UsersInviteDataEntryProps): JSX.Element => {
  const [textAreaContent, setTextAreaContent] = useState<string>(!!props.dataEntryError ? props.listOfInvitees.join(',\n') : '')
  const { t } = useTranslation()
  const { loginContext } = useLoginContext()
  const partnerId = toNullable(loginContext.partnerProfileId) ?? ''
  const stageDiveConfig = useContext<StageDiveConfig>(StageDiveConfigContext)

  const handleBlur = (): void => {
    pipe(textAreaContent, parseListOfInvitees, props.setListOfInvitees)
  }

  const handleTextAreaContentChange: ChangeEventHandler<HTMLTextAreaElement> = (event: React.ChangeEvent<HTMLTextAreaElement>): void =>
    setTextAreaContent(event.target.value)

  const errorText = (() => {
    if (props.dataEntryError === 'TooManyEntries') {
      return t(i18nKeyList.BULK_INVITE_USERS_TOO_MANY_ROWS_ERROR)
    } else if (props.dataEntryError === 'EmptyContent') {
      return t(i18nKeyList.BULK_INVITE_USERS_REQUIRED_FIELD_ERROR)
    } else {
      return undefined
    }
  })()

  useEffect(() => {
    const chromeConfig = AACChromeAPI.getChromeConfiguration()
    const pageHeader = new AACChromeAPI.PageHeader()
    const objectInfo = new AACChromeAPI.ObjectInfo()
    const actionList = new AACChromeAPI.ItemList()

    chromeConfig.setPageLayoutType('FOCUS')
    const focusModeExitEventId = 'ExitFocusView'

    const sendButton: AACChromeAPI.BUTTON = new AACChromeAPI.Button()

    const sendButtonId = 'sendButtonId'

    sendButton.setId(sendButtonId)
    sendButton.setLabel(t(i18nKeyList.BULK_INVITE_USERS_CONTINUE))
    sendButton.setButtonType(AACChromeAPI.ButtonType.NORMAL)
    sendButton.setState(textAreaContent.length === 0 ? AACChromeAPI.ActionState.DISABLED : AACChromeAPI.ActionState.ENABLED)
    const sendButtonClickFn = (): void => props.nextAction()
    AACChromeAPI.getActionEventTarget().addEventListener(sendButtonId, sendButtonClickFn)

    const backToUsersLink = new AACChromeAPI.Button()
    const backToUsersLinkButtonId = 'backToUsersLink'
    backToUsersLink.setId(backToUsersLinkButtonId)
    backToUsersLink.setLabel(t(i18nKeyList.BULK_INVITE_USERS_BACK_TO_USERS))
    backToUsersLink.setButtonType(AACChromeAPI.ButtonType.LINK)

    const backToUsersLinkFn = (): void => {
      window.location.href = getUserManagementURL(partnerId, stageDiveConfig.hostname)
    }
    AACChromeAPI.getActionEventTarget().addEventListener(backToUsersLinkButtonId, backToUsersLinkFn)
    AACChromeAPI.getCommonEventTarget().addEventListener(focusModeExitEventId, backToUsersLinkFn)

    actionList.addItem(backToUsersLink)
    actionList.addItem(sendButton)

    objectInfo.setActions(actionList)

    pageHeader.setObjectInfo(objectInfo)
    pageHeader.setPageName(t(i18nKeyList.BULK_INVITE_USERS_PAGE_TITLE))
    chromeConfig.setPageHeader(pageHeader)
    AACChromeAPI.setChromeConfiguration(chromeConfig)
    return () => {
      AACChromeAPI.getActionEventTarget().removeEventListener(sendButtonId, sendButtonClickFn)
      AACChromeAPI.getActionEventTarget().removeEventListener(backToUsersLinkButtonId, backToUsersLinkFn)
      AACChromeAPI.getCommonEventTarget().removeEventListener(focusModeExitEventId, backToUsersLinkFn)
    }
  }, [textAreaContent, props.listOfInvitees, t])

  return (
    <MainContainer>
      <ContentContainer>
        <TopAlertContainer>
          <StyledAlert
            type={'success'}
            isOpen={props.successfulInviteCount > 0}
            withCloseButton={true}
            onClose={() => {
              props.setSuccessfulInviteCount(0)
            }}
          >
            <Text>{t(i18nKeyList.BULK_INVITE_USERS_SENDING_INVITES_SUCCESS_ALERT, { inviteCount: props.successfulInviteCount })}</Text>
          </StyledAlert>
        </TopAlertContainer>
        <CardContainer>
          <StyledCard>
            <CardHeader>
              <Text textColor="base" type="h2">
                {t(i18nKeyList.BULK_INVITE_USERS_CARD_DESCRIPTION)}
              </Text>
            </CardHeader>
            <InviteDataEntryContainer>
              <FormRowContainer>
                <FormFieldNameContainer>
                  <BoldText> {t(i18nKeyList.BULK_INVITE_USERS_EMAIL_LABEL)}</BoldText>
                </FormFieldNameContainer>
                <FormFieldValueContainer>
                  <EmailFieldEntryContainer>
                    <Text type="p">{t(i18nKeyList.BULK_INVITE_USERS_EMAIL_TEXT_BOX_DESCRIPTION)}</Text>
                    <TextAreaFormGroupStyled
                      label={''}
                      aria-label={t(i18nKeyList.BULK_INVITE_USERS_DATA_ENTRY_ARIA_LABEL)}
                      id={'bulk-user-entry'}
                      onBlur={handleBlur}
                      onChange={handleTextAreaContentChange}
                      value={textAreaContent}
                      statusType={!!props.dataEntryError ? 'error' : undefined}
                      message={errorText}
                      rows={10}
                    />
                  </EmailFieldEntryContainer>
                </FormFieldValueContainer>
              </FormRowContainer>
              <UserInviteRoleInfo />
            </InviteDataEntryContainer>
          </StyledCard>
        </CardContainer>
      </ContentContainer>
    </MainContainer>
  )
}
